import { useQueryClient } from '@tanstack/react-query'
import { useAllTokens } from 'hooks/Tokens'
import { useConnection } from 'hooks/useConnection'
import { tokenPricesQueryOptions } from 'hooks/useTokenPrice'
import { tokenVolumesQueryOptions, trendingTokensQueryOptions } from 'hooks/useTrendingTokens'
import { getCurrencyBalanceQueryOptions } from 'lib/hooks/useCurrencyBalance'
import useNativeCurrency from 'lib/hooks/useNativeCurrency'
import { useEffect, useMemo } from 'react'
import { useConfig } from 'wagmi'

// Lets the requests of the page being opened go first, since they share the backend rate limit.
const PREFETCH_DELAY_MS = 3_000

/**
 * Warms the token selector's data (volumes, prices, balances) once per chain,
 * account and token set, so the selector opens without a loading state. Uses
 * prefetchQuery, which skips fresh entries and never starts polling.
 */
export default function Updater(): null {
  const queryClient = useQueryClient()
  const config = useConfig()
  const { chainId, account } = useConnection()
  const allTokens = useAllTokens()
  const native = useNativeCurrency()
  const tokens = useMemo(() => Object.values(allTokens), [allTokens])

  useEffect(() => {
    if (!chainId || tokens.length === 0) return

    const timer = window.setTimeout(() => {
      queryClient.prefetchQuery(tokenVolumesQueryOptions(chainId))
      queryClient.prefetchQuery(trendingTokensQueryOptions(chainId))
      queryClient.prefetchQuery(
        tokenPricesQueryOptions(
          tokens.map((token) => token.address),
          chainId
        )
      )
      if (!account) return
      for (const currency of [native, ...tokens]) {
        queryClient.prefetchQuery(getCurrencyBalanceQueryOptions(config, chainId, account, currency, {}))
      }
    }, PREFETCH_DELAY_MS)

    return () => window.clearTimeout(timer)
  }, [queryClient, config, chainId, account, native, tokens])

  return null
}
